/**
 * Amount coercion and currency handling.
 *
 * Money is a plain number of major units, rounded to 2 decimals exactly once —
 * at the parse boundary. Re-rounding downstream is how totals drift.
 */

import { cleanCell } from './text.ts';
import type { CurrencyCode } from './types.ts';

/**
 * Currency symbols seen on Israeli statements.
 *
 * ¥ is genuinely ambiguous between JPY and CNY. Isracard prints the symbol, not
 * a code, so we map it to JPY and the parser raises a warning: on the sample
 * statement a ¥112,533 hotel billed at ₪2,085 implies ~0.0185 ILS per unit,
 * which is JPY-shaped (CNY would be ~0.5). Verify before trusting a CNY row.
 */
export const CURRENCY_SYMBOLS: Record<string, CurrencyCode> = {
  '₪': 'ILS',
  'ש"ח': 'ILS',
  'שח': 'ILS',
  $: 'USD',
  '€': 'EUR',
  '£': 'GBP',
  '¥': 'JPY',
  '₣': 'CHF',
  '₺': 'TRY',
};

export const AMBIGUOUS_SYMBOLS = new Set(['¥']);

export function resolveCurrency(raw: string | null | undefined): CurrencyCode | null {
  if (raw === null || raw === undefined) return null;
  const value = cleanCell(String(raw));
  if (value.length === 0) return null;
  const mapped = CURRENCY_SYMBOLS[value];
  if (mapped !== undefined) return mapped;
  // Already an ISO code.
  if (/^[A-Za-z]{3}$/.test(value)) return value.toUpperCase();
  return value;
}

export function isShekel(currency: CurrencyCode | null): boolean {
  return currency === 'ILS' || currency === null;
}

export function round2(value: number): number {
  // Scale-and-round rather than toFixed: toFixed(2) on 1.005 gives "1.00".
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Parse an amount cell.
 *
 * Handles: real numeric cells, "₪ 1,234.56", "1,234.56-" (trailing minus, which
 * Israeli exports use for credits), "(1,234.56)" for negatives, and stray
 * currency symbols. Returns null when there is no number at all.
 */
export function parseAmount(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? round2(value) : null;
  }

  let text = cleanCell(String(value));
  if (text.length === 0) return null;

  let negative = false;

  if (text.startsWith('(') && text.endsWith(')')) {
    negative = true;
    text = text.slice(1, -1);
  }

  // Strip currency symbols and letters, keep digits/sign/separators.
  text = text.replace(/[^\d.,+-]/g, '');

  if (text.endsWith('-')) {
    negative = true;
    text = text.slice(0, -1);
  }
  if (text.startsWith('-')) {
    negative = true;
    text = text.slice(1);
  }
  text = text.replace(/^\+/, '');

  // Thousands separators only — Israeli statements never use ',' as decimal.
  text = text.replace(/,/g, '');

  if (text.length === 0 || !/^\d*\.?\d*$/.test(text) || !/\d/.test(text)) return null;

  const parsed = Number(text);
  if (!Number.isFinite(parsed)) return null;

  return round2(negative ? -parsed : parsed);
}

/*
 * Amounts are formatted from the *number* formatter, never the currency one,
 * and the symbol is placed by hand.
 *
 * `Intl.NumberFormat('he-IL', { style: 'currency' })` returns the symbol
 * wrapped in RLM controls — "‏36,036.14 ‏₪". Those marks force
 * an RTL run, but amounts are rendered inside `.numeric`, which sets
 * `direction: ltr` so digit groups keep their arithmetic order. The two fight,
 * and bidi resolution — not the design — decides which side the ₪ lands on.
 * Formatting the bare number and prepending the symbol makes the result
 * identical in an RTL or an LTR context.
 */
const ILS_NUMBER = new Intl.NumberFormat('he-IL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const ILS_NUMBER_COMPACT = new Intl.NumberFormat('he-IL', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** U+200E/U+200F and the embedding/isolate controls. */
const BIDI_CONTROLS = /[‎‏‪-‮⁦-⁩]/g;

const SHEKEL = '₪';

function stripBidiControls(value: string): string {
  return value.replace(BIDI_CONTROLS, '');
}

/** The sign leads the symbol: -₪120.50, not ₪-120.50. */
function withSymbol(symbol: string, formatted: string, negative: boolean): string {
  return `${negative ? '-' : ''}${symbol}${stripBidiControls(formatted)}`;
}

export function formatILS(value: number, compact = false): string {
  const format = compact ? ILS_NUMBER_COMPACT : ILS_NUMBER;
  return withSymbol(SHEKEL, format.format(Math.abs(value)), value < 0);
}

export function formatCurrency(value: number, currency: CurrencyCode): string {
  const absolute = Math.abs(value);
  const negative = value < 0;

  try {
    // The symbol for a non-ILS currency has to come from Intl — there is no
    // table of them here — so read it off the parts rather than the whole
    // string, which carries the same bidi controls as the ILS one.
    const parts = new Intl.NumberFormat('he-IL', { style: 'currency', currency }).formatToParts(
      absolute,
    );
    const symbol = parts.find((part) => part.type === 'currency')?.value ?? currency;
    const digits = parts
      .filter((part) => ['integer', 'group', 'decimal', 'fraction'].includes(part.type))
      .map((part) => part.value)
      .join('');
    return withSymbol(stripBidiControls(symbol), digits, negative);
  } catch {
    // Unresolvable code (we kept a raw symbol) — fall back to a plain number.
    return withSymbol('', `${new Intl.NumberFormat('he-IL').format(absolute)} ${currency}`, negative);
  }
}
